import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, MapPin, Shield, Heart, ArrowRight, Search, 
  Compass, Globe, Star, CheckCircle, Home, Bookmark,
  User, Sparkles, MessageCircle, UserPlus, Calendar,
  ChevronRight, Lock, Zap, Loader2, Plane
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const HERO_IMAGE = 'https://images.unsplash.com/photo-1621314450340-1ff21fa983e4?w=1200&q=80';

const FALLBACK_DESTINATIONS = [
  { name: 'Goa', travelers: '2.4K', image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80' },
  { name: 'Ladakh', travelers: '1.8K', image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80' },
  { name: 'Kerala', travelers: '1.5K', image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=400&q=80' },
  { name: 'Rajasthan', travelers: '2.1K', image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=400&q=80' },
  { name: 'Manali', travelers: '1.2K', image: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=400&q=80' },
  { name: 'Varanasi', travelers: '890', image: 'https://images.unsplash.com/photo-1561361513-2d000a50f0dc?w=400&q=80' },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Set Your Destination',
    description: 'Tell us where and when you want to travel. AI matches you with travelers heading the same way.',
    icon: MapPin,
    color: 'from-teal-500 to-cyan-500',
    shadow: 'shadow-teal-500/30',
  },
  {
    step: '02',
    title: 'Get Matched by Travel DNA',
    description: 'Our AI analyzes travel styles, budgets, and interests to find your ideal travel companions.',
    icon: Zap,
    color: 'from-amber-500 to-orange-500',
    shadow: 'shadow-amber-500/30',
  },
  {
    step: '03',
    title: 'Connect Safely',
    description: 'Send invite requests using pre-built templates. No personal info shared until both sides accept.',
    icon: Shield,
    color: 'from-violet-500 to-purple-500',
    shadow: 'shadow-violet-500/30',
  },
  {
    step: '04',
    title: 'Plan Together',
    description: 'Once connected, co-plan your itinerary using Panchi AI. Split costs, share experiences.',
    icon: Calendar,
    color: 'from-rose-500 to-pink-500',
    shadow: 'shadow-rose-500/30',
  },
];

const SAFETY_FEATURES = [
  { icon: Lock, title: 'Anonymous Until Match', text: 'No names, photos, or personal details shown until mutual acceptance.' },
  { icon: Shield, title: 'Verified Profiles', text: 'Email verification and opt-in discovery keeps the community genuine.' },
  { icon: MessageCircle, title: 'Template Messages Only', text: 'Pre-defined invite messages prevent spam and inappropriate contact.' },
  { icon: Users, title: 'Block & Report', text: 'Full control over your connections with instant block and report.' },
];

const TESTIMONIALS = [
  { name: 'Priya S.', location: 'Mumbai', text: 'Found an amazing travel buddy for my Ladakh trip. We had the same budget and travel style!', rating: 5 },
  { name: 'Arjun M.', location: 'Bangalore', text: 'Solo travel is great, but sharing a sunset in Goa with someone who gets it? Even better.', rating: 5 },
  { name: 'Sneha K.', location: 'Delhi', text: 'The privacy features made me feel safe. No creepy messages, just genuine travelers.', rating: 5 },
];

const FAQ_DATA = [
  { q: 'Is it free to find a travel buddy?', a: 'Yes! Basic buddy matching is completely free. Pro members get priority matching, advanced filters, and unlimited connections.' },
  { q: 'How does Panchi protect my privacy?', a: 'Your profile is completely anonymous until you mutually accept a connection. No names, photos, or personal details are shared during the matching phase.' },
  { q: 'Can I find travel buddies for international trips?', a: 'Absolutely! While Panchi is popular in India, our community spans globally. You can find travel companions for any destination worldwide.' },
  { q: 'What if I change my mind about a travel buddy?', a: 'You can disconnect from any buddy at any time. Use the block feature if needed — no questions asked.' },
  { q: 'How does the AI matching work?', a: 'Our Travel DNA algorithm considers your travel style (adventure, relaxed, luxury, budget), interests, preferred dates, budget range, and destination preferences to find compatible companions.' },
];

export default function FindTravelBuddy() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [searchDest, setSearchDest] = useState('');
  const [travelEvents, setTravelEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      setLoadingEvents(true);
      try {
        const res = await api.getAITravelWorthyEvents(6);
        if (res?.success && res?.events?.length > 0) {
          setTravelEvents(res.events.slice(0, 6));
        } else {
          const fallback = await api.getAITrendingEvents(6);
          if (fallback?.success && fallback?.events?.length > 0) {
            setTravelEvents(fallback.events.slice(0, 6));
          }
        }
      } catch (err) {
        console.error('Error loading travel events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/community?tab=trips');
    } else {
      navigate('/auth?redirect=/community');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="find-travel-buddy-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <PanchiBirdLogo className="w-10 h-10" />
            <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Panchi</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 p-1 bg-gray-100" style={{ borderRadius: '9999px' }}>
            {[
              { to: '/', icon: Home, label: 'Home' },
              { to: '/community', icon: Users, label: 'Community' },
              { to: '/planning', icon: Compass, label: 'Plan Trip' },
            ].map(item => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center gap-2 px-5 py-2 text-gray-600 hover:text-teal-600 transition-all"
                style={{ borderRadius: '9999px' }}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
          <button
            onClick={handleGetStarted}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all"
            style={{ borderRadius: '9999px' }}
            data-testid="header-find-buddy-btn"
          >
            <UserPlus size={18} />
            <span className="hidden sm:inline">Find a Buddy</span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 px-4 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-teal-50" />
        <div className="absolute top-20 -left-20 w-72 md:w-96 h-72 md:h-96 bg-teal-200/40 blur-3xl" style={{ borderRadius: '50%' }} />
        <div className="absolute bottom-10 -right-20 w-72 md:w-96 h-72 md:h-96 bg-cyan-200/40 blur-3xl" style={{ borderRadius: '50%' }} />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left */}
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-teal-200 shadow-sm mb-6"
                style={{ borderRadius: '9999px' }}
              >
                <Users size={16} className="text-teal-600" />
                <span className="text-xs md:text-sm font-semibold text-gray-700">Trusted by Solo Travelers Across India</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-5 text-gray-900"
                data-testid="hero-heading"
              >
                Find Your Perfect{' '}
                <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 bg-clip-text text-transparent">
                  Travel Buddy
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-base md:text-lg text-gray-600 max-w-xl mx-auto lg:mx-0 mb-8"
              >
                AI-powered matching connects you with like-minded travelers heading to the same destination. 
                Safe, anonymous, and completely free.
              </motion.p>

              {/* Search Box */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto lg:mx-0 mb-8"
              >
                <div className="relative flex-1">
                  <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Where do you want to go?"
                    value={searchDest}
                    onChange={(e) => setSearchDest(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-white border-2 border-gray-200 focus:border-teal-500 outline-none text-gray-800 font-medium transition-all"
                    style={{ borderRadius: '9999px' }}
                    data-testid="buddy-search-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchDest.trim()) {
                        navigate(`/community?tab=trips&search=${encodeURIComponent(searchDest.trim())}`);
                      }
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    if (searchDest.trim()) {
                      navigate(`/community?tab=trips&search=${encodeURIComponent(searchDest.trim())}`);
                    } else {
                      handleGetStarted();
                    }
                  }}
                  className="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-xl shadow-teal-500/30 hover:shadow-2xl hover:scale-105 transition-all whitespace-nowrap"
                  style={{ borderRadius: '9999px' }}
                  data-testid="buddy-search-btn"
                >
                  Find Buddies
                </button>
              </motion.div>

              {/* Quick Stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap justify-center lg:justify-start gap-6"
              >
                {[
                  { value: '5K+', label: 'Active Travelers' },
                  { value: '92%', label: 'Match Rate' },
                  { value: '100%', label: 'Privacy First' },
                ].map((stat, i) => (
                  <div key={i} className="text-center lg:text-left">
                    <div className="text-xl md:text-2xl font-black text-gray-900">{stat.value}</div>
                    <span className="text-xs md:text-sm text-gray-500">{stat.label}</span>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right - Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="relative hidden lg:block"
            >
              <div className="relative overflow-hidden shadow-2xl" style={{ borderRadius: '2rem' }}>
                <img
                  src={HERO_IMAGE}
                  alt="Travel buddies exploring together"
                  className="w-full h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="p-4 bg-white/90 backdrop-blur-md" style={{ borderRadius: '1rem' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white" style={{ borderRadius: '50%' }}>
                        <CheckCircle size={20} />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">New Match Found!</p>
                        <p className="text-xs text-gray-500">Adventure traveler heading to Ladakh, Jun 15-22</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating card */}
              <motion.div
                className="absolute -top-4 -right-4 p-4 bg-white shadow-xl flex items-center gap-3"
                style={{ borderRadius: '1rem' }}
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                  <Heart size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">98% Match</p>
                  <p className="text-xs text-gray-500">Same travel DNA</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Upcoming Events to Plan Travel Around */}
      <section className="py-16 px-4 bg-gray-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 text-sm font-bold mb-4" style={{ borderRadius: '9999px' }}>
              <Calendar size={16} />
              PLAN YOUR HOLIDAY
            </span>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-3">
              Upcoming Events to <span className="text-teal-600">Travel For</span>
            </h2>
            <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
              Festivals, sports, holidays & experiences worth planning a trip around
            </p>
          </div>

          {loadingEvents ? (
            <div className="flex justify-center py-12">
              <Loader2 size={32} className="animate-spin text-teal-500" />
            </div>
          ) : travelEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {travelEvents.map((event, index) => (
                <motion.div
                  key={event.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="group relative overflow-hidden shadow-lg hover:shadow-xl transition-all bg-white border border-gray-100"
                  style={{ borderRadius: '1.25rem' }}
                  data-testid={`travel-event-${index}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={event.image || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80'}
                      alt={event.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80'; e.target.onerror = null; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <span className="absolute top-2 right-2 px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[10px] md:text-xs font-bold text-gray-700 capitalize" style={{ borderRadius: '9999px' }}>
                      {event.category || 'Event'}
                    </span>
                    {event.days_until && event.days_until <= 30 && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold" style={{ borderRadius: '9999px' }}>
                        In {event.days_until} days
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-1">{event.name}</h3>
                    <p className="text-xs text-gray-500 mb-2 line-clamp-1">{event.description}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <MapPin size={12} />
                      <span>{event.city}, {event.country}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-semibold text-teal-600">
                        <Calendar size={12} />
                        <span>{event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Upcoming'}</span>
                      </div>
                      <button
                        onClick={() => navigate(`/planning?destination=${encodeURIComponent(event.city)}&event=${encodeURIComponent(event.name)}`)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                        style={{ borderRadius: '9999px' }}
                        data-testid={`plan-event-trip-${index}`}
                      >
                        <Plane size={12} />
                        Plan Trip
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {FALLBACK_DESTINATIONS.map((dest, index) => (
                <motion.button
                  key={dest.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  whileHover={{ y: -8 }}
                  onClick={() => navigate(`/community?tab=trips&search=${encodeURIComponent(dest.name)}`)}
                  className="group relative aspect-[3/4] overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-shadow"
                  style={{ borderRadius: '1.25rem' }}
                  data-testid={`buddy-dest-${dest.name.toLowerCase()}`}
                >
                  <img src={dest.image} alt={`Find travel buddies for ${dest.name}`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-white font-bold text-base md:text-lg">{dest.name}</h3>
                    <div className="flex items-center gap-1 text-white/80 text-xs">
                      <Users size={12} />
                      <span>{dest.travelers} travelers</span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 text-sm font-bold mb-4" style={{ borderRadius: '9999px' }}>
              <Sparkles size={16} />
              SIMPLE & SAFE
            </span>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-3">
              How It <span className="text-teal-600">Works</span>
            </h2>
            <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
              From search to shared adventure in four simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative p-6 bg-white border border-gray-100 shadow-lg hover:shadow-2xl transition-all group"
                style={{ borderRadius: '1.5rem' }}
                data-testid={`how-it-works-step-${step.step}`}
              >
                <div className="absolute -top-3 -left-3 w-10 h-10 bg-gray-900 text-white flex items-center justify-center text-sm font-black" style={{ borderRadius: '50%' }}>
                  {step.step}
                </div>
                <div className={`w-14 h-14 bg-gradient-to-br ${step.color} flex items-center justify-center mb-5 shadow-lg ${step.shadow}`} style={{ borderRadius: '1rem' }}>
                  <step.icon size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-white via-teal-50/30 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 text-sm font-bold mb-6" style={{ borderRadius: '9999px' }}>
                <Shield size={16} />
                SAFETY FIRST
              </span>
              <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-4">
                Your Privacy is <span className="text-teal-600">Non-Negotiable</span>
              </h2>
              <p className="text-gray-500 text-base md:text-lg mb-8">
                We built Panchi with a privacy-first approach. No personal information is shared 
                until both travelers mutually agree to connect.
              </p>

              <div className="space-y-4">
                {SAFETY_FEATURES.map((feature, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all"
                    style={{ borderRadius: '1rem' }}
                    data-testid={`safety-feature-${i}`}
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 flex-shrink-0 flex items-center justify-center text-white" style={{ borderRadius: '0.75rem' }}>
                      <feature.icon size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{feature.title}</h4>
                      <p className="text-sm text-gray-500">{feature.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative hidden lg:block"
            >
              <div className="p-8 bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100" style={{ borderRadius: '2rem' }}>
                {/* Mock buddy profile card */}
                <div className="space-y-4">
                  <div className="p-5 bg-white shadow-md" style={{ borderRadius: '1.25rem' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center" style={{ borderRadius: '50%' }}>
                        <Compass size={24} className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Adventure Explorer</p>
                        <p className="text-xs text-gray-500">Travel DNA: Adventure + Photography</p>
                      </div>
                      <div className="ml-auto px-3 py-1 bg-green-100 text-green-700 text-xs font-bold" style={{ borderRadius: '9999px' }}>
                        95% Match
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {['Ladakh', 'Jun 15-22', 'Budget: Mid-range'].map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold" style={{ borderRadius: '9999px' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button className="flex-1 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-bold" style={{ borderRadius: '0.75rem' }}>
                        Send Invite
                      </button>
                      <button className="py-2.5 px-4 border-2 border-gray-200 text-gray-500 text-sm font-bold" style={{ borderRadius: '0.75rem' }}>
                        Skip
                      </button>
                    </div>
                  </div>

                  <div className="p-5 bg-white shadow-md" style={{ borderRadius: '1.25rem' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center" style={{ borderRadius: '50%' }}>
                        <Star size={24} className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Culture Seeker</p>
                        <p className="text-xs text-gray-500">Travel DNA: Cultural + Foodie</p>
                      </div>
                      <div className="ml-auto px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold" style={{ borderRadius: '9999px' }}>
                        87% Match
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['Rajasthan', 'Jul 1-7', 'Budget: Low'].map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-semibold" style={{ borderRadius: '9999px' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 text-rose-700 text-sm font-bold mb-4" style={{ borderRadius: '9999px' }}>
              <Heart size={16} />
              REAL STORIES
            </span>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-3">
              Travelers Who <span className="text-teal-600">Connected</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-all"
                style={{ borderRadius: '1.5rem' }}
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={16} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic leading-relaxed">"{t.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center text-white text-xs font-bold" style={{ borderRadius: '50%' }}>
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 bg-gray-50/50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-3">
              Frequently Asked <span className="text-teal-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-3">
            {FAQ_DATA.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-100 shadow-sm overflow-hidden"
                style={{ borderRadius: '1rem' }}
                data-testid={`faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-bold text-gray-900 pr-4">{faq.q}</span>
                  <ChevronRight
                    size={20}
                    className={`text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-90' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-500 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative p-10 md:p-16 overflow-hidden text-center"
            style={{
              borderRadius: '2.5rem',
              background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 25%, #99f6e4 50%, #5eead4 75%, #2dd4bf 100%)'
            }}
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/30 blur-2xl" style={{ borderRadius: '50%' }} />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/30 blur-2xl" style={{ borderRadius: '50%' }} />

            <div className="relative z-10">
              <PanchiBirdLogo className="w-16 h-16 mx-auto mb-6" />
              <h2 className="text-2xl md:text-4xl font-black text-teal-900 mb-4">
                Your Next Adventure Starts with a Buddy
              </h2>
              <p className="text-teal-800/70 text-base md:text-lg mb-8 max-w-xl mx-auto">
                Join thousands of travelers who found their perfect travel companion through Panchi.
              </p>
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center gap-3 px-10 py-5 bg-white text-teal-700 font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="cta-find-buddy-btn"
              >
                <Users size={22} />
                Find Your Travel Buddy
                <ArrowRight size={20} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-3">
              <PanchiBirdLogo className="w-10 h-10" />
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Panchi</span>
                <span className="block text-xs text-gray-500">Fly Free, Explore More</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              {[
                { label: 'Home', to: '/' },
                { label: 'Plan a Trip', to: '/planning' },
                { label: 'Community', to: '/community' },
                { label: 'Blog', to: '/blog' },
                { label: 'About', to: '/about' },
                { label: 'Privacy', to: '/privacy' },
                { label: 'Terms', to: '/terms' },
              ].map(link => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-gray-500 hover:text-teal-600 transition-colors font-medium"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <p className="text-sm text-gray-400">
              &copy; 2026 Panchi. Made for travelers.
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-teal-600">
            <Users size={22} />
            <span className="text-xs font-medium">Buddies</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
